import React, { useState, useCallback } from 'react'
import { useImperativeHandle } from 'react'
import useForm from '../../../hooks/useForm'
import Button from '../button/Button'
import Clickable from '../clickable/Clickable'
import Input from '../form/input/Input'
import InlineGroup from '../inlinegroup/InlineGroup'
import VerticalGroup from '../verticalgroup/VerticalGroup'

import Text, { Props as TextProps } from './Text'

import './EditableText.scss'

interface Props extends Omit<TextProps, 'children'> {
  'data-test'?: string
  value: string | number
  type?: 'text' | 'textbox'
  onChange: (value: string) => void
  formatter?: ((value: string) => string) | ((value: number) => string)
  alignment?: 'horizontal' | 'vertical'
  clickableStyle?: React.CSSProperties
}

export interface Handles {
  setIsEditing: (is: boolean) => void
}

const defaultFormatter = (str: string) => str

const EditableText: React.ForwardRefRenderFunction<Handles, Props> = ((
  {
    value,
    onChange,
    type='textbox',
    alignment = 'vertical',
    formatter = defaultFormatter,
    clickableStyle,
    ...rest
  },
  ref
) => {
  const [isEditing, setIsEditing] = useState(false)
  const [{ formData: {updatedValue} }, { setField, setFieldRaw }] = useForm({ updatedValue: null })

  useImperativeHandle(ref, () => ({ setIsEditing }))

  const handleCancel = useCallback(() => {
    setFieldRaw('updatedValue', null)
    setIsEditing(false)
  }, [setFieldRaw])

  const handleSave = useCallback(() => {
    onChange(updatedValue)
    setIsEditing(false)
  }, [onChange, updatedValue])

  if (!isEditing) {
    return (
      <Clickable onClick={() => setIsEditing(true)} withHover style={clickableStyle}>
        {/* @ts-expect-error */}
        <Text {...rest} dangerouslySetInnerHTML={{ __html: formatter(updatedValue ?? value) }} />
      </Clickable>
    )
  }

  const content = (
    <>
      <InlineGroup spaceLeft={2}>
        <Input
          block
          type={type}
          value={updatedValue ?? value}
          onChange={setField('updatedValue')}
        />
      </InlineGroup>
      <InlineGroup spaceLeft={2}>
        <Button shadow onClick={handleSave}>Save</Button>
        <Button shadow light onClick={handleCancel} style={{ backgroundColor: 'white' }}>Cancel</Button>
      </InlineGroup>
    </>
  )

  return alignment === 'vertical' ? (
    <VerticalGroup spaceBetweenElements={2} className='editabletext'>
      {content}
    </VerticalGroup>
  ) : (
    <InlineGroup spaceLeft={2} className='editabletext'>
      {content}
    </InlineGroup>
  )
})

export default React.forwardRef(EditableText)
